import { useMemo, useState } from "react";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { AdminPageShell } from "@/components/shared/admin-page-shell";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { FaqCategoryDialog } from "@/features/faqs/components/FaqCategoryDialog";
import { FaqQuestionDialog } from "@/features/faqs/components/FaqQuestionDialog";
import {
  useCreateFaqCategory,
  useCreateFaqQuestion,
  useDeleteFaqCategory,
  useDeleteFaqQuestion,
  useFaqCategories,
  useFaqPermissions,
  useFaqQuestions,
  useUpdateFaqCategory,
  useUpdateFaqQuestion,
} from "@/features/faqs/hooks/useFaqs";
import type { FaqCategory, FaqQuestion } from "@/types/faq";

/**
 * الأسئلة الشائعة — the content behind /Open-Budget-Survey-questionnaire on the public site.
 *
 * The page has NO entry point anywhere on the site; it is reached by typing the URL. Everything
 * on it is edited here, gated on `faq:manage` (SUPER_ADMIN-only by baseline, tickable per role
 * directly under OBS in the permission panel).
 *
 * 🔴 THE THING TO EXPLAIN TO WHOEVER USES THIS SCREEN: 215 questions were imported in ENGLISH
 * ONLY and with NO answers. Until a question has Arabic text AND its category has an Arabic
 * title, it does not appear on the Arabic page at all — which is why the Arabic page is empty
 * today and the English one is complete. That is the intended starting state, not a fault.
 */
export default function FaqsPage() {
  const { canManageFaqs } = useFaqPermissions();
  const categoriesQuery = useFaqCategories();
  const categories = useMemo(() => categoriesQuery.data ?? [], [categoriesQuery.data]);

  const [activeKey, setActiveKey] = useState<string>("");
  const [search, setSearch] = useState("");
  const selectedKey = activeKey || categories[0]?.key || "";

  // Filtering by category keeps every list inside one page — the largest category holds 53
  // questions and the backend caps a page at 100, so nothing here silently truncates.
  const questionsQuery = useFaqQuestions({
    category: search ? undefined : selectedKey || undefined,
    search: search || undefined,
    page: 1,
    pageSize: 100,
  });
  const questions = questionsQuery.data?.items ?? [];
  const total = questionsQuery.data?.meta.total ?? 0;

  const createQuestion = useCreateFaqQuestion();
  const updateQuestion = useUpdateFaqQuestion();
  const deleteQuestion = useDeleteFaqQuestion();
  const createCategory = useCreateFaqCategory();
  const updateCategory = useUpdateFaqCategory();
  const deleteCategory = useDeleteFaqCategory();

  const [questionDialog, setQuestionDialog] = useState<{ open: boolean; row: FaqQuestion | null }>({
    open: false,
    row: null,
  });
  const [categoryDialog, setCategoryDialog] = useState<{ open: boolean; row: FaqCategory | null }>({
    open: false,
    row: null,
  });

  const savingQuestion = createQuestion.isPending || updateQuestion.isPending;
  const savingCategory = createCategory.isPending || updateCategory.isPending;

  if (!canManageFaqs) {
    return (
      <AdminPageShell title="الأسئلة الشائعة">
        <div className="rounded-lg border border-border bg-muted/30 p-6 text-center text-sm text-muted-foreground">
          لا تملك صلاحية إدارة الأسئلة الشائعة.
        </div>
      </AdminPageShell>
    );
  }

  return (
    <AdminPageShell
      title="الأسئلة الشائعة"
      description="إدارة أسئلة وإجابات صفحة /Open-Budget-Survey-questionnaire. الصفحة غير مرتبطة بأي رابط في الموقع وتُفتح بالعنوان مباشرة."
      isLoading={categoriesQuery.isLoading}
      error={(categoriesQuery.error ?? questionsQuery.error) as Error | null}
    >
      <div className="space-y-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-2">
            <Input
              value={search}
              placeholder="ابحث برمز السؤال أو نصّه…"
              className="w-64"
              onChange={(e) => setSearch(e.target.value)}
            />
            {search && (
              <span className="text-sm text-muted-foreground">
                نتائج البحث في كل الأقسام: {total}
              </span>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={() => setCategoryDialog({ open: true, row: null })}>
              <Plus className="ms-1 size-4" /> قسم جديد
            </Button>
            <Button
              onClick={() => setQuestionDialog({ open: true, row: null })}
              disabled={categories.length === 0}
            >
              <Plus className="ms-1 size-4" /> سؤال جديد
            </Button>
          </div>
        </div>

        <div className="grid gap-6 lg:grid-cols-[18rem_1fr]">
          <aside className="space-y-1">
            {categories.map((c) => (
              <div
                key={c.key}
                className={`flex items-center justify-between gap-2 rounded-lg border p-2 ${
                  c.key === selectedKey && !search
                    ? "border-primary bg-primary/5"
                    : "border-border"
                }`}
              >
                <button
                  type="button"
                  className="flex-1 text-right text-sm"
                  onClick={() => {
                    setSearch("");
                    setActiveKey(c.key);
                  }}
                >
                  <span className="block font-medium">
                    {c.title.ar || c.title.en || c.key}
                  </span>
                  <span className="block text-xs text-muted-foreground" dir="ltr">
                    {c.key} · قسم {c.sectionNumber}
                    {c.shortCode ? ` · ${c.shortCode}` : ""}
                  </span>
                  {!c.title.ar && (
                    // The rule people trip on: no Arabic title => the whole panel, questions
                    // included, is absent from the Arabic page however many questions are translated.
                    <span className="mt-1 block text-xs text-amber-600">
                      بلا عنوان عربي — لن يظهر القسم على الصفحة العربية
                    </span>
                  )}
                </button>
                <div className="flex shrink-0 gap-1">
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label="تعديل القسم"
                    onClick={() => setCategoryDialog({ open: true, row: c })}
                  >
                    <Pencil className="size-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label="حذف القسم"
                    onClick={() => {
                      if (window.confirm(`حذف القسم «${c.title.ar || c.title.en || c.key}»؟`)) {
                        deleteCategory.mutate(c.key);
                      }
                    }}
                  >
                    <Trash2 className="size-4" />
                  </Button>
                </div>
              </div>
            ))}
            {categories.length === 0 && !categoriesQuery.isLoading && (
              <p className="text-sm text-muted-foreground">لا توجد أقسام بعد.</p>
            )}
          </aside>

          <div className="rounded-lg border border-border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-28">الرمز</TableHead>
                  <TableHead>السؤال</TableHead>
                  <TableHead className="w-40">الحالة</TableHead>
                  <TableHead className="w-28">إجراءات</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {questions.map((q) => (
                  <TableRow key={q.code}>
                    <TableCell dir="ltr" className="font-mono text-xs">
                      {q.code}
                    </TableCell>
                    <TableCell>
                      <span className="block text-sm">{q.question.ar || "—"}</span>
                      <span className="block text-xs text-muted-foreground" dir="ltr">
                        {q.question.en || "—"}
                      </span>
                    </TableCell>
                    <TableCell className="space-x-1 space-x-reverse">
                      {/* An empty answer is a real, expected state — all 215 imported rows start
                          here — so it is labelled plainly rather than flagged as an error. */}
                      {q.answer.ar || q.answer.en ? (
                        <Badge variant="secondary">فيها إجابة</Badge>
                      ) : (
                        <Badge variant="outline">بلا إجابة</Badge>
                      )}
                      {!q.question.ar && <Badge variant="outline">بلا ترجمة</Badge>}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          aria-label="تعديل السؤال"
                          onClick={() => setQuestionDialog({ open: true, row: q })}
                        >
                          <Pencil className="size-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          aria-label="حذف السؤال"
                          onClick={() => {
                            if (window.confirm(`حذف السؤال «${q.code}»؟`)) {
                              deleteQuestion.mutate(q.code);
                            }
                          }}
                        >
                          <Trash2 className="size-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {questions.length === 0 && !questionsQuery.isLoading && (
                  <TableRow>
                    <TableCell colSpan={4} className="py-8 text-center text-sm text-muted-foreground">
                      لا توجد أسئلة في هذا القسم.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </div>
      </div>

      <FaqQuestionDialog
        open={questionDialog.open}
        onOpenChange={(open) => setQuestionDialog((s) => ({ ...s, open }))}
        categories={categories}
        question={questionDialog.row}
        defaultCategoryKey={selectedKey}
        isSaving={savingQuestion}
        onSubmit={(values) => {
          const done = { onSuccess: () => setQuestionDialog({ open: false, row: null }) };
          if (questionDialog.row) {
            const { code, ...rest } = values;
            void code;
            updateQuestion.mutate({ code: questionDialog.row.code, input: rest }, done);
          } else {
            createQuestion.mutate(values, done);
          }
        }}
      />

      <FaqCategoryDialog
        open={categoryDialog.open}
        onOpenChange={(open) => setCategoryDialog((s) => ({ ...s, open }))}
        category={categoryDialog.row}
        isSaving={savingCategory}
        onSubmit={(values) => {
          const done = { onSuccess: () => setCategoryDialog({ open: false, row: null }) };
          if (categoryDialog.row) {
            const { key, ...rest } = values;
            void key;
            updateCategory.mutate({ key: categoryDialog.row.key, input: rest }, done);
          } else {
            createCategory.mutate(values, done);
          }
        }}
      />
    </AdminPageShell>
  );
}
